<template>
  <div
    class="flex h-full w-full overflow-y-auto sm:mb-0"
    :class="[{ 'shadow-lg': !config.isEmbedded }, isMobile ? 'flex-col bg-white' : 'bg-light-grey']"
  >
    <platform-nav-bar v-if="showNav" @close="mobileNavIsToggled = false" />
    <main
      data-cy="borrower-platform-main"
      class="flex h-full w-full flex-col rounded-lg bg-white"
      :class="[
        {
          relative: config.isWidget,
        },
        isLoggedIn && selectedApplication && [isMobile ? '' : 'max-w-[calc(100%-8rem)]'],
      ]"
    >
      <template v-if="!isBrandingLoading && !showWidget">
        <div v-if="isMobile" class="m-2 mb-0">
          <platform-header-mobile
            :show-stage-details="showStageDetailsInMobileHeader"
            @hamburger:click="mobileNavIsToggled = true"
          />
        </div>
        <platform-header-desktop v-else-if="!isMobile && screenHasDesktopHeader" />
      </template>
      <platform-contact
        v-if="isMobile && selectedApplication?.funding_advisor && !showWidget"
        class="absolute right-5 bottom-10"
        :advisor="selectedApplication.funding_advisor"
      />
      <div v-if="isWidgetOpen" v-show="showWidget" class="flex h-full w-full">
        <iframe
          ref="widgetIframe"
          :src="widgetUrl"
          sandbox="allow-scripts allow-same-origin allow-forms allow-popups"
          class="h-full w-full flex-1"
          data-cy="widget-iframe"
        />
      </div>
      <div
        v-show="!showWidget"
        class="relative h-full w-full overflow-auto"
        :class="{ 'mb-20': showStickyMobileFooter && Boolean(mobileFooter?.context) }"
      >
        <div class="flex min-h-full flex-col">
          <div class="flex min-h-3/4 flex-1 flex-col">
            <slot></slot>
          </div>
          <platform-footer
            v-if="isLoggedIn && showFooter && activePlatformTemplate?.content.footer"
            :footer="activePlatformTemplate.content.footer"
          />
        </div>
      </div>
      <div
        v-if="config.isWidget && showFloatingButton && !showWidget"
        class="absolute right-4 bottom-4 z-10"
      >
        <platform-floating-button />
      </div>
    </main>
    <div v-if="!config.isWidget && !showWidget" class="fixed bottom-0 z-10 w-full">
      <platform-sticky-mobile-footer v-if="showStickyMobileFooter" ref="mobileFooter" />
      <div class="relative" :class="showStickyMobileFooter ? 'bottom-20' : 'bottom-4'">
        <platform-floating-button v-if="showFloatingButton" />
      </div>
    </div>
  </div>
</template>

<script setup lang="ts">
import { computed, ref, useTemplateRef, watch } from "vue";
import { useEventListener } from "@vueuse/core";
import config from "@/config";
import { useResponsive } from "@/composables/common";
import useApplicationsState from "@/composables/state/applicationsState";
import useWidgetModalState from "@/composables/state/widgetModalState";
import { WIDGET_EVENTS } from "@lendflow/utils/widget-events";
import PlatformFooter from "@/components/platform/PlatformFooter.vue";
import useAuthState from "@/composables/state/authState";
import useBrandingState from "@/composables/state/brandingState";
import { useChatState } from "@/composables/state/chatSessionState";
import usePlatformScreenState from "@/composables/state/platformScreenState";
import { TabType, WelcomePageType } from "@/api/http/schemas/borrowerPlatformTemplatesSchemas";

import PlatformFloatingButton from "@/components/platform/platform-floating-button/PlatformFloatingButton.vue";
import PlatformContact from "@/components/platform/PlatformContact.vue";
import PlatformStickyMobileFooter from "@/components/platform/PlatformStickyMobileFooter.vue";
import PlatformHeaderMobile from "@/components/platform/header/PlatformHeaderMobile.vue";
import PlatformHeaderDesktop from "@/components/platform/header/PlatformHeaderDesktop.vue";
import PlatformNavBar from "@/components/platform/PlatformNavBar.vue";

const { isMobile } = useResponsive();
const {
  isOpen: isWidgetOpen,
  url: widgetUrl,
  origin: widgetOrigin,
  close: closeWidget,
  open: openWidget,
} = useWidgetModalState();

const widgetIframe = useTemplateRef<HTMLIFrameElement>("widgetIframe");

useEventListener(window, "message", (event: MessageEvent) => {
  if (!widgetOrigin.value || event.origin !== widgetOrigin.value) {
    return;
  }

  if (event.source !== widgetIframe.value?.contentWindow) {
    return;
  }

  const eventName = event.data?.eventName;

  if (eventName === WIDGET_EVENTS.DESTROY || eventName === WIDGET_EVENTS.INITIALIZE_BP) {
    closeWidget();
    return;
  }

  if (eventName === WIDGET_EVENTS.REDIRECT && event.data?.url) {
    openWidget(event.data.url);
  }
});

const { selectedApplication, showOffersList } = useApplicationsState();
const {
  screenHasDesktopHeader,
  selectedTab,
  activePlatformTemplate,
  welcomeScreenTab,
  hasVisibleNavItems,
} = usePlatformScreenState();
const { isBrandingLoading } = useBrandingState();
const { isLoggedIn } = useAuthState();
const { hasSession: hasChatSession } = useChatState();
const mobileFooter =
  useTemplateRef<InstanceType<typeof PlatformStickyMobileFooter>>("mobileFooter");

const mobileNavIsToggled = ref(false);

const showNav = computed(
  () => hasVisibleNavItems.value && (!isMobile.value || mobileNavIsToggled.value)
);

const showStageDetailsInMobileHeader = computed(() => selectedTab.value?.type === TabType.workflow);

const showWidget = computed(
  () => isWidgetOpen.value && selectedTab.value?.type === TabType.workflow
);

const showFloatingButton = computed(() => {
  if (!hasChatSession.value) {
    if (isMobile.value) {
      return Boolean(selectedApplication.value?.funding_advisor);
    }

    return false;
  }

  if (selectedTab.value?.type === TabType.profile) {
    return false;
  }

  if (selectedTab.value?.type === TabType.welcomePage) {
    return welcomeScreenTab.value?.mode !== WelcomePageType.AiAgent;
  }

  return true;
});

const showStickyMobileFooter = computed(() => isMobile.value && !showOffersList.value);

const showFooter = computed(
  () =>
    activePlatformTemplate.value?.content.is_footer_enabled &&
    activePlatformTemplate.value?.content.footer
);

watch(isMobile, (newIsMobile) => {
  if (!newIsMobile) {
    mobileNavIsToggled.value = false;
  }
});
</script>
